import { useFormikContext } from "formik";
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSwitch } from "../../context/SwitchContext";
import EN from "../../locales/en";
import scrollToFirstError from "../../utils/scrollToFirstError";
import Button from "../Button";

const NavigationButtons = ({ generatingSwitch, priceId, mpan, mprn }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentPage } = location.state;

  const { validateForm, setFieldTouched, isSubmitting, isValidating, errors } =
    useFormikContext();
  const { setShowSwitchingFormErrorsAlert } = useSwitch();
  const isValidatingPreSubmit = isSubmitting && isValidating;

  useEffect(() => {
    const scrollToErrors = async () => {
      const errors = await validateForm();
      if (Object.keys(errors).length > 0) {
        scrollToFirstError(errors);
        setShowSwitchingFormErrorsAlert(true);
        return;
      }
    };

    if (isValidatingPreSubmit) {
      scrollToErrors();
    }
  }, [isValidatingPreSubmit]); // eslint-disable-line react-hooks/exhaustive-deps

  // if all current pages form errors are cleared, hide the errors alert
  useEffect(() => {
    let hasErrors = false;
    if (currentPage === 1) {
      hasErrors = firstPageFields.some((field) => {
        const fieldPath = field.split(".");
        return errors[fieldPath[0]]?.[fieldPath[1]];
      });
    } else {
      hasErrors = Object.keys(errors).length > 0;
    }
    if (!hasErrors) {
      setShowSwitchingFormErrorsAlert(false);
    }
  }, [errors, location]); // eslint-disable-line react-hooks/exhaustive-deps

  const firstPageFields = [
    "customerInfo.moveInDate",
    "customerInfo.custTitle",
    "customerInfo.custForename",
    "customerInfo.custLastName",
    "customerInfo.custDateofBirth",
    "customerInfo.custEmail1",
    "customerInfo.confirmEmail",
    "customerInfo.custLandline1",
    "paymentInfo.bankAccountName",
    "paymentInfo.bankAccountNumber",
    "paymentInfo.bankSortCode",
    "paymentInfo.detailsConfirmed",
    "paymentInfo.accountHolderConfirmed",
    "paymentInfo.monthlyPaymentsDate",
  ];

  const handleNext = async (event) => {
    event.preventDefault();
    const formErrors = await validateForm();
    const hasErrors = firstPageFields.some((field) => {
      const fieldPath = field.split(".");
      return formErrors[fieldPath[0]]?.[fieldPath[1]];
    });
    if (hasErrors) {
      firstPageFields.forEach((field) => {
        setFieldTouched(field, true);
        scrollToFirstError(formErrors);
        setShowSwitchingFormErrorsAlert(true);
      });
      return;
    }
    navigate(`/switch/${priceId}/${mpan}/${mprn}`, {
      replace: false,
      state: { currentPage: 2 },
    }); // Add history entry for page 1 so the user can go back
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="flex justify-between gap-6 mt-8">
      <Button type="button" variant="tertiary" fullWidth onClick={handleBack}>
        Back
      </Button>

      {currentPage === 1 ? (
        <Button
          type="button"
          fullWidth
          disabled={isSubmitting || generatingSwitch}
          onClick={handleNext}
        >
          Next
        </Button>
      ) : (
        <Button
          type="submit"
          fullWidth
          disabled={isSubmitting || generatingSwitch}
        >
          {generatingSwitch
            ? EN.switch.generating_switch
            : EN.switch.switch_supplier}
        </Button>
      )}
    </div>
  );
};

export default NavigationButtons;
